import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAutosaveStagedDefinition } from './useAutosaveStagedDefinition';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseVariableEditAutosaveResult, VariableEditSaveDialogProps } from '../types';

// Applies a variable delete or reorder to the list at once, then saves it. While
// the save runs the list is blocked by a dialog; a failed save can be retried, or
// dismissed to revert the edit.
export const useVariableEditAutosave = (): UseVariableEditAutosaveResult => {
  const { formatMessage } = useIntl();
  const { projectId } = useAnalysisRouteParams();
  const stageRemoveSource = useAnalysisDefinitionStore((state) => state.removeSource);
  const stageReorderSources = useAnalysisDefinitionStore((state) => state.reorderSources);
  const revertStaged = useAnalysisDefinitionStore((state) => state.revert);
  const { saveStaged, isSaving, saveError, resetSaveError } =
    useAutosaveStagedDefinition(projectId);

  const removeSource = useCallback(
    (sourceKey: string) => {
      stageRemoveSource(sourceKey);
      saveStaged();
    },
    [stageRemoveSource, saveStaged]
  );
  const reorderSources = useCallback(
    (activeKey: string, overKey: string) => {
      stageReorderSources(activeKey, overKey);
      saveStaged();
    },
    [stageReorderSources, saveStaged]
  );

  // Nothing else shows or clears a staged edit, so dismissing a failed save puts the list back.
  const dismissError = useCallback(() => {
    revertStaged();
    resetSaveError();
  }, [revertStaged, resetSaveError]);

  const saveDialogProps = useMemo<VariableEditSaveDialogProps>(
    () => ({
      isSaving,
      hasError: saveError !== null,
      messages: {
        savingTitle: formatMessage({ id: 'analysisDetail.variableEditSave.savingTitle' }),
        savingText: formatMessage({ id: 'analysisDetail.variableEditSave.savingText' }),
        errorTitle: formatMessage({ id: 'analysisDetail.variableEditSave.errorTitle' }),
        errorText: formatMessage({ id: 'analysisDetail.variableEditSave.errorText' }),
        retryLabel: formatMessage({ id: 'analysisDetail.variableEditSave.retry' }),
        dismissLabel: formatMessage({ id: 'analysisDetail.variableEditSave.dismiss' }),
      },
      onRetry: saveStaged,
      onDismiss: dismissError,
    }),
    [isSaving, saveError, formatMessage, saveStaged, dismissError]
  );

  return { removeSource, reorderSources, saveDialogProps };
};
